import { Component, OnInit, Output, EventEmitter, Input, ChangeDetectorRef } from '@angular/core';
import { GridsterItem } from 'angular-gridster2';
import { WidgetsHelper } from '../widgets.helper';
import { Subject } from 'rxjs/internal/Subject';
import { debounceTime } from 'rxjs/operators';
import { CustomGridsterConfig } from '../../../models/widget-configuration.model';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'interface-widget-grid',
  template: `<gridster [options]="options" class="editable">
  <ng-container *ngFor="let item of dashboard; let index = index;">
    <div *ngIf="options?.editable && active?.index === index" class="backdrop"></div>
    <div class="grid-container" [ngClass]="{'focused': active?.index === index}">
      <gridster-item [item]="item">
        <div class="gridster-item-content">
          <div *ngIf="options?.editable && active?.index !== index" class="actions">
            <i class="action fa fa-times" (click)="removeItem($event, item)"></i>
            <i class="action fa fa-cog" (click)="configureItem($event, item, index)"></i>
          </div>
          <div *ngIf="item['meta']" class="content">
            <div class="cover" *ngIf="options?.editable" [ngClass]="{'drag-handler': active?.index !== index}"></div>
            <interface-dynamic-renderer [component]="item['meta']?.name" [item]="item"></interface-dynamic-renderer>
          </div>
        </div>
      </gridster-item>
    </div>
  </ng-container>
</gridster>
`,
  styles: [`@-webkit-keyframes centering{0%{left:0;right:0;bottom:100%;top:100%;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}25%{left:0;right:0;bottom:50%;top:50%;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}50%{left:0;right:0;bottom:25%;top:25%;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}75%{left:0;right:0;bottom:15%;top:15%;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}100%{left:0;right:0;bottom:0;top:0;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}}@keyframes centering{0%{left:0;right:0;bottom:100%;top:100%;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}25%{left:0;right:0;bottom:50%;top:50%;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}50%{left:0;right:0;bottom:25%;top:25%;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}75%{left:0;right:0;bottom:15%;top:15%;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}100%{left:0;right:0;bottom:0;top:0;display:block;margin:auto;-webkit-transform:none!important;transform:none!important}}.actions{position:absolute;top:5px;right:5px;z-index:112;opacity:.9}.actions .action{padding:5px 7px;margin:0 3px;border:solid transparent;background-color:#ccc}.red{color:red}.gridster-item-content{height:100%}.gridster-item-content .content{position:relative;height:100%}.gridster-item-content .content .cover{position:absolute;top:0;bottom:0;left:0;right:0;background-color:transparent;z-index:111}.drag-handler{margin:0 5px}.grid-container.focused{position:absolute;top:0;bottom:0;left:0;right:0;vertical-align:middle;margin:0 auto;height:100%;width:100%;z-index:102}.grid-container.focused gridster-item{position:absolute;right:0;left:0;bottom:0;top:0;display:block;margin:auto!important;-webkit-animation-name:centering;animation-name:centering;-webkit-animation-duration:3s;animation-duration:3s;-webkit-transform:none!important;transform:none!important}.backdrop{z-index:101;background-color:rgba(0,0,0,.3);position:absolute;top:0;right:0;bottom:0;left:0}`],
})
export class GridComponent implements OnInit {
  options: CustomGridsterConfig;

  @Input() item: CustomGridsterConfig;
  @Input() dashboard: Array<GridsterItem> = [];
  @Input() set reset(bool: Boolean) {
    if (bool === true) {
      this._activeItem = undefined;
    }
  }
  private _editable: boolean;
  @Input()
  set editable(editable) {
    this._editable = editable;
    this.loadOptions();
    this.ref.detectChanges();
  }
  get editable() {
    return this._editable;
  }

  @Output('targetConfig') configEmitter = new EventEmitter<any>();
  private _activeItem: any;
  private _saveSubject = new Subject<any>();

  constructor(
    private ref: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.loadOptions();
    this._saveSubject.subscribe(() => {
      // Update active index
      if (this.active) {
        const item = this.active.item;
        const index = (this.dashboard || []).indexOf(item);
        if (!item || index === -1) {
          this._activeItem = undefined;
          return this.configEmitter.emit(this._activeItem);
        }
        this._activeItem = { item, index };
      }
    });
    this._saveSubject.pipe(debounceTime(500)).subscribe(() => {
      localStorage.setItem(WidgetsHelper.dashboardKey, JSON.stringify(this.dashboard));
    });
  }

  get active() {
    return this._activeItem;
  }

  saveToLocal() {
    this._saveSubject.next();
  }

  configureItem($event, item, index) {
    $event.preventDefault();
    $event.stopPropagation();

    this._activeItem = { item, index };
    this.configEmitter.emit(item);
  }

  emptyCellClick(event: MouseEvent, item: GridsterItem) {
    const widgetData = event['dataTransfer'].getData('widgetData');
    const options = event['dataTransfer'].getData('options');
    item.meta = JSON.parse(widgetData);
    const finalItem = item;
    if (options) {
      Object.assign(finalItem, JSON.parse(options));
    }
    this.dashboard.push(finalItem);
  }

  removeItem($event, item) {
    $event.preventDefault();
    $event.stopPropagation();

    const itemIndex = this.dashboard.indexOf(item);
    if (this.active === itemIndex) {
      this.configEmitter.emit(null);
    }
    this.dashboard.splice(itemIndex, 1);
  }

  addItem() {
    this.dashboard.push({ x: 0, y: 0, cols: 1, rows: 1 });
  }

  private loadOptions() {
    this.options = Object.assign(WidgetsHelper.defaultGridConfiguration(this.editable), {
      emptyCellDropCallback: this.emptyCellClick.bind(this),
      itemChangeCallback: this.saveToLocal.bind(this),
      itemResizeCallback: this.saveToLocal.bind(this),
      itemRemovedCallback: this.saveToLocal.bind(this),
    });
  }
}
